import { Component, OnInit, OnDestroy, Input } from "@angular/core";

import { SampleService } from "./sample.service";
import { LoggedUser } from "src/app/auth/logged-user.model";
import { Subscription } from "rxjs";
import { AuthService } from "src/app/auth/auth.service";

import { Translations } from "../../localization/translations.service";
import { TranslateService } from "@ngx-translate/core";
import * as pbi from "powerbi-client";
import { ActivatedRoute, Router } from "@angular/router";
import { loginFilterValuesForEmbed } from "../pbi-embed-login.util";

@Component({
  selector: "sample",
  templateUrl: "./sample.component.html",
  styleUrls: ["./sample.component.css"],
})
export class SampleComponent implements OnInit, OnDestroy {
  public showLoading: boolean = true;
  public showNoData: boolean = false;
  public RequestData: any;
  public LoggedUser: LoggedUser;

  public reportId = this.route.snapshot.data["reportId"];
  public groupId = this.route.snapshot.data["groupId"];
  public pageName = this.route.snapshot.data["pageName"];
  public datasetId = this.route.snapshot.data["datasetId"];
  public hideSidebar = this.route.snapshot.data["hideSidebar"];
  private loginFilterValues: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private pbiService: SampleService,
    private authService: AuthService,
    public t10s: Translations,
    public translateService: TranslateService
  ) {}
  private dataSub: Subscription;

  ngOnInit() {
    this.getUserLogged();
    this.getToken();
  }

  ngOnDestroy() {
    if (this.dataSub) this.dataSub.unsubscribe();
  }

  getToken() {
    this.dataSub = this.pbiService
      .getReportEmbedTokenListener()
      .subscribe((data: any) => {
        this.RequestData = data;
        this.ShowReport();
      });

    this.pbiService.getReportEmbedToken(
      this.reportId,
      this.groupId,
      this.datasetId
    );
  }

  getUserLogged() {
    this.LoggedUser = this.authService.getLoggedUser();
    const internal = this.authService.isInternalUser(this.LoggedUser);
    this.loginFilterValues = this.isDashboardPbiRoute()
      ? loginFilterValuesForEmbed(this.LoggedUser, internal)
      : [];
  }

  private isDashboardPbiRoute(): boolean {
    const url = (this.router.url || "").split("?")[0].trim().toLowerCase();
    if (url === "/dashboard" || url === "dashboard" || /^\/dashboard\/?$/.test(url)) {
      return true;
    }
    let r: ActivatedRoute | null = this.route;
    while (r) {
      const p = (r.routeConfig && r.routeConfig.path) || "";
      const seg = p.replace(/^\//, "").replace(/\/$/, "").toLowerCase();
      if (seg === "dashboard") return true;
      r = r.firstChild;
    }
    return false;
  }

  ShowReport() {
    let configEmbedded: pbi.service.IComponentEmbedConfiguration;
    const isInternal = this.authService.isInternalUser(this.LoggedUser);
    let not_normal_configurations = {
      type: "report",
      tokenType: pbi.models.TokenType.Embed,
      id: this.reportId,
      embedUrl: this.buildEmbedUrl(this.reportId, this.groupId),
      accessToken: this.RequestData.accessToken,
      pageName: this.pageName,
    };

    let normal_configurations = {
      type: "report",
      tokenType: pbi.models.TokenType.Embed,
      id: this.reportId,
      embedUrl: this.buildEmbedUrl(this.reportId, this.groupId),
      accessToken: this.RequestData.accessToken,
      pageName: this.pageName,
    };

    if (!isInternal) {
      configEmbedded = {
        ...normal_configurations,
        settings: {
          filterPaneEnabled: false,
          navContentPaneEnabled: false,
        },
      };
    } else {
      configEmbedded = {
        ...not_normal_configurations,
        settings: {
          filterPaneEnabled: true,
          navContentPaneEnabled: false,
        },
      };
    }

    const reportsContainer = <HTMLElement>(
      document.getElementById("embedReport")
    );

    let powerbi = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    );

    let report = powerbi.embed(reportsContainer, configEmbedded);

    report.on("loaded", async () => {
      await this.applyLoginFilterIfExists(report);
      this.showLoading = false;
    });
  }

  private async applyLoginFilterIfExists(report: any) {
    if (!this.loginFilterValues.length) return;

    try {
      const reportFilters = await report.getFilters();
      const reportLoginFilter = this.findLoginFilter(reportFilters);
      if (reportLoginFilter && reportLoginFilter.target) {
        await report.updateFilters(
          pbi.models.FiltersOperations.Replace,
          this.mergeLoginFilter(reportFilters, reportLoginFilter.target, this.loginFilterValues)
        );
      }

      const pages = await report.getPages();
      for (const page of pages || []) {
        const pageFilters = await page.getFilters();
        const pageLoginFilter = this.findLoginFilter(pageFilters);
        if (pageLoginFilter && pageLoginFilter.target) {
          await page.updateFilters(
            pbi.models.FiltersOperations.Replace,
            this.mergeLoginFilter(pageFilters, pageLoginFilter.target, this.loginFilterValues)
          );
        }
      }
    } catch (e) {
      console.warn("[SampleComponent] Falha ao aplicar filtro de login automaticamente:", e);
    }
  }

  private findLoginFilter(filters: any[]): any | null {
    return (filters || []).find((f: any) => {
      const col = f && f.target && f.target.column;
      return col && String(col).toLowerCase() === "login";
    }) || null;
  }

  private buildForcedLoginFilter(target: any, values: string[]) {
    const uniq = Array.from(new Set((values || []).filter(Boolean)));
    return {
      $schema: "http://powerbi.com/product/schema#basic",
      target,
      operator: "In",
      values: uniq,
      filterType: pbi.models.FilterType.Basic,
      requireSingleSelection: true,
    };
  }

  private buildEmbedUrl(reportId: string, groupId: string): string {
    return (
      "https://app.powerbi.com/reportEmbed?reportId=" +
      reportId +
      "&groupId=" +
      groupId
    );
  }

  private mergeLoginFilter(filters: any[], target: any, values: string[]) {
    const otherFilters = (filters || []).filter((f: any) => !this.isSameFilterTarget(f && f.target, target));
    return [...otherFilters, this.buildForcedLoginFilter(target, values)];
  }

  private isSameFilterTarget(left: any, right: any): boolean {
    const leftTable = String((left && left.table) || "").toLowerCase();
    const leftColumn = String((left && left.column) || "").toLowerCase();
    const rightTable = String((right && right.table) || "").toLowerCase();
    const rightColumn = String((right && right.column) || "").toLowerCase();
    return leftTable === rightTable && leftColumn === rightColumn;
  }

}
